// VER-218: VerifyU Console login page (two-phase: password -> email OTP).
// Wired to useSession (login -> verifyOtp -> resendOtp). On success lands in the
// existing dashboard shell (/dashboard) via the 3a bridge.
import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { App, Button, Card, Form, Input, Typography } from "antd";
import { useSession } from "../console/useSession";
import OtpInput from "../console/OtpInput";
import { activeBrand } from "../brand/brandConfig";

const { Title, Text } = Typography;

// Prefer the backend's structured error; fall back to a plain Error's message
// (e.g. the cross-brand BrandMismatchError), then a generic default.
const errorMessage = (err) =>
  err?.response?.data?.error?.message ||
  err?.message ||
  "Something went wrong. Please try again.";

export default function ConsoleLogin() {
  const { login, verifyOtp, resendOtp } = useSession();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const brand = activeBrand(); // brand for THIS endpoint's hostname

  const [phase, setPhase] = useState("credentials"); // credentials | otp
  const [otpToken, setOtpToken] = useState(null);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []); // cleanup on unmount

  const startResendCooldown = () => {
    setResendIn(30);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendIn((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const handleCredentials = async ({ email: em, password }) => {
    setLoading(true);
    try {
      const data = await login(em.trim(), password);
      setOtpToken(data.otp_token);
      setEmail(em.trim());
      setCode("");
      setPhase("otp");
      startResendCooldown();
      message.success("We emailed you a 6-digit code.");
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (fullCode) => {
    setLoading(true);
    try {
      await verifyOtp(otpToken, fullCode);
      message.success("Signed in.");
      navigate("/console", { replace: true });
    } catch (err) {
      message.error(errorMessage(err));
      setCode("");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      await resendOtp(otpToken);
      startResendCooldown();
      message.success("New code sent.");
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: 24 }}>
      <Card style={{ width: 400 }}>
        {/* Endpoint brand (from hostname). Cross-brand accounts are rejected after
            OTP by the session guard, so this brand is authoritative for this URL. */}
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={brand.displayName}
              style={{ height: brand.logoHeight || 40, display: "block", margin: "0 auto" }}
            />
          ) : (
            <Title level={3} style={{ margin: 0 }}>
              {brand.displayName}
            </Title>
          )}
          {brand.logo && brand.showNameWithLogo && (
            <Title level={4} style={{ margin: "8px 0 0" }}>
              {brand.displayName}
            </Title>
          )}
        </div>

        {phase === "credentials" ? (
          <>
            <Text type="secondary">Sign in to your account</Text>
            <Form
              layout="vertical"
              onFinish={handleCredentials}
              style={{ marginTop: 16 }}
              disabled={loading}
              requiredMark={false}
            >
              <Form.Item
                name="email"
                label="Email"
                rules={[{ required: true, type: "email", message: "Enter a valid email" }]}
              >
                <Input placeholder="you@company.com" autoComplete="username" size="large" />
              </Form.Item>
              <Form.Item
                name="password"
                label="Password"
                rules={[{ required: true, message: "Enter your password" }]}
              >
                <Input.Password placeholder="••••••••" autoComplete="current-password" size="large" />
              </Form.Item>
              <Button type="primary" htmlType="submit" block size="large" loading={loading}>
                Continue
              </Button>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Link to="/console/forgot-password">Forgot password?</Link>
              </div>
            </Form>
          </>
        ) : (
          <>
            <Text type="secondary">
              Enter the 6-digit code sent to <b>{email}</b>
            </Text>
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 16 }}>
              <OtpInput value={code} onChange={setCode} onComplete={handleVerify} disabled={loading} />
              <Button
                type="primary"
                block
                size="large"
                loading={loading}
                disabled={code.length !== 6}
                onClick={() => handleVerify(code)}
              >
                Verify &amp; sign in
              </Button>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <Button
                  type="link"
                  size="small"
                  onClick={() => {
                    setPhase("credentials");
                    setCode("");
                  }}
                >
                  ← Back
                </Button>
                <Button type="link" size="small" disabled={resendIn > 0} onClick={handleResend}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
